/**
 * This file contains main window commands for pull requests.
 *
 * To add another command, define a new subclass and append the
 * class to the default export at the bottom of the file.
 */
import {EditIssueBody, EditIssueTitle} from './issue'
import {ClipboardCopyElement} from '@github/clipboard-copy-element'
import type CommandPalette from '../command-palette-element'
import {MainWindowCommand} from '../main-window-command'
import {OpenCodespace} from './codespaces'
import {copyText} from '../copy'

class EditPullRequestBody extends EditIssueBody {
  override title = 'Edit pull request body'

  pullRequestBody() {
    return document.querySelector('.js-command-palette-pull-body')
  }

  isPullRequest() {
    return !!this.pullRequestBody()
  }

  override isApplicable() {
    return this.isPullRequest()
  }
}

class EditPullRequestTitle extends EditIssueTitle {
  override title = 'Edit pull request title'

  pullRequestBody() {
    return document.querySelector('.js-command-palette-pull-body')
  }

  isPullRequest() {
    return !!this.pullRequestBody()
  }

  override isApplicable() {
    return this.fetchButton() instanceof HTMLButtonElement && this.isPullRequest()
  }
}

class UpdateBranch extends MainWindowCommand {
  override title = 'Update current branch'
  override icon = 'sync-color-fg-muted'

  override isApplicable() {
    return this.fetchButton() instanceof HTMLButtonElement
  }

  fetchButton() {
    return document.querySelector<HTMLButtonElement>('.js-update-branch-form button')
  }

  override run() {
    const button = this.fetchButton()

    if (button) {
      button.scrollIntoView({behavior: 'smooth', block: 'center'})
      button.click()
    }
  }
}

class ConvertToDraft extends MainWindowCommand {
  override title = 'Convert to draft'
  override icon = 'git-pull-request-draft-color-fg-muted'

  override isApplicable() {
    return this.fetchButton() instanceof HTMLButtonElement
  }

  fetchButton() {
    return document.querySelector<HTMLButtonElement>('.js-convert-to-draft')
  }

  override run() {
    const details = this.fetchButton()?.closest<HTMLDetailsElement>('details')

    if (details) {
      details.open = true

      setTimeout(() => {
        details.querySelector<HTMLButtonElement>('.js-convert-to-draft')?.focus()
      }, 0)
    }
  }
}

class CopyBranchName extends MainWindowCommand {
  override title = 'Copy current branch name'
  override icon = 'copy-color-fg-muted'

  override isApplicable() {
    return this.fetchClipboardCopy() instanceof ClipboardCopyElement
  }

  fetchClipboardCopy() {
    return document.querySelector('.js-copy-branch')
  }

  override async run(commandPalette: CommandPalette) {
    const cloneContainer = this.fetchClipboardCopy()
    if (cloneContainer instanceof ClipboardCopyElement) {
      const url = cloneContainer.value
      try {
        await copyText(url)

        // eslint-disable-next-line i18n-text/no-en
        commandPalette.displayFlash('success', 'Branch name copied to clipboard!')
      } catch {
        // eslint-disable-next-line i18n-text/no-en
        commandPalette.displayFlash('error', 'Unable to copy branch name to clipboard!')
      }
    }
  }
}

export default [CopyBranchName, EditPullRequestTitle, EditPullRequestBody, UpdateBranch, ConvertToDraft, OpenCodespace]
